import useIsMounted from "../../../hooks/use-is-mounted";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import Dialog from "../../base/dialog";
import {Box, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import Button from "../../base/button/button";
import Grid2 from "@mui/material/Unstable_Grid2";
import {refundTransactionsDataGridColumns} from "../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../packages/data-grid";
import PartnerDataGrid from "../../../containers/data-grid";
import ToastService from "../../../../core/services/toast";

const dataGridSaveConfig = {
    name: 'RefundTransactionsDialogDataGrid',
    version: 1,
};

const RefundTransactionsDialog = ({
                                      open,
                                      close,
                                      transactions,
                                      refundTransaction: refundTransactionProp,
                                  }) => {
    const [removing, setRemoving] = useState(null);
    const isMounted = useIsMounted();

    /**
     * Resets the removing state when the dialog is opened.
     */
    useEffect(() => {
        if (open) {
            setRemoving(null);
        }
    }, [open]);

    /**
     * Automatically closes the dialog when there are no transactions to refund.
     */
    useEffect(() => {
        if (!transactions?.length) {
            close();
        }
    }, [transactions?.length, close]);

    /**
     * Warns the user when the main API is used in the development environment.
     */
    useEffect(() => {
        if (open) {
            ToastService.mainAPIRedZoneWarning();
        }
    }, [open])

    /**
     * Refunds a transaction.
     * @type {(function(*): Promise<void>)|*}
     */
    const refundTransaction = useCallback(async (transaction) => {
        setRemoving(transaction.id);
        await refundTransactionProp?.(transaction);
        if (!isMounted())
            return;
        setRemoving(null);
    }, [isMounted, refundTransactionProp]);

    const columns = useMemo(() =>
        /**@type {DataGridColumn[]}*/
        [
            {
                title: 'Transaction No',
                name: refundTransactionsDataGridColumns.transactionNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 170,
            },
            {
                title: 'Type',
                name: refundTransactionsDataGridColumns.transactionTypeDescription,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: 'Amount',
                name: refundTransactionsDataGridColumns.amount,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.money,
                sortable: false,
            },
            {
                title: 'Submitted Date',
                name: refundTransactionsDataGridColumns.submittedDate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
                sortable: false,
            },
            {
                title: 'Service',
                name: refundTransactionsDataGridColumns.service,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Authorization Code",
                name: refundTransactionsDataGridColumns.authorizationCode,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 170,
            },
            {
                title: 'Status',
                name: refundTransactionsDataGridColumns.status,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                name: refundTransactionsDataGridColumns.select,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinned: true,
                pinnedToggleable: false,
                pinnedType: DataGridColumnPinnedTypes.right,
                width: 170,
            },
        ], [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig?.name,
        version: dataGridSaveConfig?.version,
        sortByObjectMap: {
            property: 'field',
            descending: 'descending',
        },
    });

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return transactions
                ?.map((transaction) => {
                    return {
                        key: transaction.id,
                        data: transaction,
                        cells: {
                            [refundTransactionsDataGridColumns.transactionNo]: transaction.transactionNo,
                            [refundTransactionsDataGridColumns.transactionTypeDescription]: transaction.transactionTypeDescription,
                            [refundTransactionsDataGridColumns.amount]: transaction.amount,
                            [refundTransactionsDataGridColumns.submittedDate]: transaction.submittedDate,
                            [refundTransactionsDataGridColumns.service]: transaction.service?.title,
                            [refundTransactionsDataGridColumns.authorizationCode]: transaction.authorizationCode,
                            [refundTransactionsDataGridColumns.status]: transaction.status,
                            [refundTransactionsDataGridColumns.select]:
                                <Button
                                    disabled={!!removing}
                                    variant={'outlined'}
                                    color={'primary'}
                                    onClick={() => refundTransaction(transaction)}
                                >
                                    {
                                        transaction.id === removing
                                            ? 'processing...'
                                            : 'Refund this transaction'
                                    }
                                </Button>,
                        }
                    }
                })
            ?? []
    }, [transactions, removing, refundTransaction]);

    return (
        <Dialog open={open} setOpen={close} maxWidth={'md'} loading={removing}>
            <DialogTitle>
                Refund Transactions
            </DialogTitle>
            <DialogContent dividers>
                <Grid2 spacing={2} container>
                    <Grid2 xs={12}>
                        <Typography variant={'body2'}>
                            Refund the submitted transactions by clicking the "Refund this transaction" button.
                        </Typography>
                    </Grid2>
                    <Grid2 xs={12}>
                        <PartnerDataGrid
                            hideFooterActions
                            hideExporting
                            initialLayoutState={{
                                columns: columns,
                            }}
                            classNames={{
                                container: "px-0",
                                toolbar: 'pt-0',
                            }}
                            rows={dataGridRows}
                            columns={dataGridColumns}
                            state={{
                                pagination: {currentPage: 1, pageSize: transactions.length},
                                density: dataGridDensity,
                            }}
                        />
                    </Grid2>
                </Grid2>
            </DialogContent>
            <DialogActions className={'p-0'}>
                <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <Button
                        disabled={removing}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => close()}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}
export default RefundTransactionsDialog;
